import { NullableId } from "@common/types/interface";
import { AttributionData } from "@common/utils/attributionReader";
import DeepDiff from "deep-diff";
import moment, { Moment } from "moment";

export interface MenuState {
  openChatByPhoneNumber: boolean;
  scheduledMessages: boolean;
  broadcastBulkCampaign: boolean;
}

export interface BetaFeatures {
  backendSocket?: boolean;
}

export interface UserInterface {
  _id?: string;
  email?: string;
  fullName?: string;
  profileImg?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;
  address?: string;
  countryCode?: string;
  vatId?: string;
  phone?: string;
  password?: string;
  peerMap?: {
    [key: string]: Date;
  };
  createdAt?: Moment;
  updatedAt?: Moment;
  unlimited?: boolean;
  apiKey?: string;
  menuState?: MenuState;
  WASession?: string;
  qr?: string;
  qrStatus?: string;
  defaultChatCountryCode?: string;
  beta?: BetaFeatures;
  lastSessionCheck?: string;
  sessionIsActive?: boolean;
  notifications?: {
    badSocket?: {
      count: number;
      lastNotification: string;
    };
  };
  accessToken?: string;
  currentWorkspace?: NullableId;
  isDarkMode?: boolean;
  direction?: string;
  isBoardsPanelOpen?: boolean;
  /** Keep the WhatsApp Web tab awake (undefined = enabled). See keep-awake-control. */
  keepWhatsappTabAwake?: boolean;
  /** btgpt "Files auto-recognition" permission (undefined = enabled): a file
   *  sent in a linked AI chat is auto-forwarded to the agent, which identifies
   *  it (invoice → auto-file to the Invoices board; else ask what to do). Off:
   *  files are ignored until the user replies on them / asks explicitly. */
  btgptFilesAutoRecognition?: boolean;
  impersonating?: boolean;
  googleId?: string;
  invoiceDetails?: {
    firstName?: string;
    lastName?: string;
    address?: string;
    address2?: string;
    companyName?: string;
    zipCode?: string;
    country?: string;
    city?: string;
    vatId?: string;
    zip?: string;
  },
  isEmailConfirmed?: boolean;
  emailConfirmationToken?: string;
  role?: string;
  /** Server-computed (read-only) capability, rev 3.3 §6.5: the owner has at
   *  least one Claude agent or operator chat. When true the extension forwards
   *  every new WhatsApp message (inbound AND fromMe) to the backend — see
   *  bt-common wa-events/wa-event-relevance.ts RelevanceContext.claudeAgentsActive. */
  claudeAgentsActive?: boolean;
  preferences?: {
    calendarStartDay?: number;
    defaultSchedulingTime?: string; // "current" or "HH:mm" format
    /** FE#535 — display-only clock format; unset = the interface language's default. */
    timeFormat?: '12h' | '24h';
    /** #max-send-window — cross-surface default for the campaign "skip late messages" window.
     *  Persisted at campaign launch (remember-last-choice); null/undefined = off. */
    defaultMaxSendWindowMinutes?: number | null;
    /** First visit of the web connect wizard (/onboarding) — the calendar auto-opens it only while unset. */
    onboardingSeenAt?: string;
    /** The first-week setup row, per account (web + extension). */
    firstWeek?: { dismissedAt?: string; completedAt?: string; seenAt?: string };
    /** Publish the owner's own outgoing WhatsApp messages (fromMe) to MQTT.
     *  Off by default — see bt-common wa-events/wa-event-relevance.ts. */
    mqttIncludeOwnMessages?: boolean;
    /** Publish inbound WhatsApp messages to MQTT even with no webhook
     *  configured — see bt-common wa-events/wa-event-relevance.ts
     *  RelevanceContext.mqttIncludeInboundMessages. */
    mqttIncludeInboundMessages?: boolean;
  };
  preferredLanguage?: string;
  defaultAIPrompt?: string;
  bulkVersion?: "migrating" | "migrated";
  waVersion?: string;
  analytics_data?: {
    mauticId?: string;
    [key: string]: any;
  };
  // Temporary properties for attribution tracking
  attribution?: AttributionData;
  trackAttribution?: boolean;
  fcmToken?: string;
  isLabelsDisabled?: boolean;
  /** Message Vault backup configuration (see message-vault-backup design, contract C1).
   *  Edited from /account-backup; engines pick changes up via normal user-doc sync. */
  backup?: {
    vaultEnabled?: boolean;        // default true
    dbBackupEnabled?: boolean;     // default false (opt-in)
    vaultRetentionDays?: number;   // default 2 (2-30)
    backupIntervalHours?: number;  // default 24
    mediaTtlDays?: number;         // default 7
    media?: {
      audio?: boolean;             // default true
      images?: boolean;            // default true
      documents?: boolean;         // default true
      videos?: boolean;            // default false
      archives?: boolean;          // default false
      maxMb?: number;              // default 15
    };
    ui?: { showCountdown?: boolean }; // default true
    /** Debug (bt-debug): force the interval (minutes) in any context; 0 = off. */
    testIntervalMinutes?: number;
    /** Dedicated-server (gateway pod) overrides — ON + 15-min by default. Debug-gated in UI. */
    gateway?: {
      vaultEnabled?: boolean;
      dbBackupEnabled?: boolean;           // default false (admin opts a gateway in)
      vaultRetentionDays?: number;
      backupIntervalMinutes?: number;      // default 15
      mediaTtlDays?: number;
      media?: {
        audio?: boolean;
        images?: boolean;
        documents?: boolean;
        videos?: boolean;
        archives?: boolean;
        maxMb?: number;
      };
    };
    /** Set by the backend ingest after-hook on a successful backup upload (ms epoch or ISO). Read-only in UI. */
    lastBackupAt?: number | string;
    /** Debug "Back up now": ms epoch the settings page stamps to trigger a one-off
     *  engine backup (bypasses idle/due gates). 0/undefined = none. */
    forceRunAt?: number;
  };
}
export class User {
  _id?: string;
  email?: string;
  fullName?: string;
  profileImg?: string;
  firstName?: string;
  lastName?: string;

  phone?: string;
  password?: string;
  peerMap?: {
    [key: string]: Date;
  };
  createdAt: Moment;
  updatedAt: Moment;
  unlimited?: boolean;
  apiKey?: string;
  menuState?: MenuState;
  WASession?: string;
  qr?: string;
  qrStatus?: string;
  defaultChatCountryCode?: string;
  beta?: BetaFeatures;
  lastSessionCheck?: string;
  sessionIsActive?: boolean;
  notifications?: {
    badSocket?: {
      count: number;
      lastNotification: string;
    };
  };
  accessToken?: string;
  currentWorkspace?: NullableId;
  isDarkMode?: boolean;
  isBoardsPanelOpen?: boolean;
  role?: string;
  /** Server-computed (read-only) capability, rev 3.3 §6.5: the owner has at
   *  least one Claude agent or operator chat. When true the extension forwards
   *  every new WhatsApp message (inbound AND fromMe) to the backend — see
   *  bt-common wa-events/wa-event-relevance.ts RelevanceContext.claudeAgentsActive. */
  claudeAgentsActive?: boolean;
  preferences?: {
    calendarStartDay?: number;
    defaultSchedulingTime?: string; // "current" or "HH:mm" format
    /** FE#535 — display-only clock format; unset = the interface language's default. */
    timeFormat?: '12h' | '24h';
    /** #max-send-window — cross-surface default for the campaign "skip late messages" window.
     *  Persisted at campaign launch (remember-last-choice); null/undefined = off. */
    defaultMaxSendWindowMinutes?: number | null;
    /** First visit of the web connect wizard (/onboarding) — the calendar auto-opens it only while unset. */
    onboardingSeenAt?: string;
    /** The first-week setup row, per account (web + extension). */
    firstWeek?: { dismissedAt?: string; completedAt?: string; seenAt?: string };
    /** Publish the owner's own outgoing WhatsApp messages (fromMe) to MQTT.
     *  Off by default — see bt-common wa-events/wa-event-relevance.ts. */
    mqttIncludeOwnMessages?: boolean;
    /** Publish inbound WhatsApp messages to MQTT even with no webhook
     *  configured — see bt-common wa-events/wa-event-relevance.ts
     *  RelevanceContext.mqttIncludeInboundMessages. */
    mqttIncludeInboundMessages?: boolean;
  };
  preferredLanguage?: string;
  defaultAIPrompt?: string;
  chargeVAT?: boolean;
  VATPercent?: string;
  invoiceDetails?: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    address?: string;
    zipCode?: string;
    country?: string;
    city?: string;
    vatId?: string;
  }
  waVersion?: string;
  isLabelsDisabled?: boolean;
  /** Keep the WhatsApp Web tab awake (undefined = enabled). See keep-awake-control. */
  keepWhatsappTabAwake?: boolean;
  /** btgpt "Files auto-recognition" (undefined = enabled). See user interface doc above. */
  btgptFilesAutoRecognition?: boolean;

  constructor(data: UserInterface) {
    Object.assign(this, data);

    this.createdAt = moment(data.createdAt);
    this.updatedAt = moment(data.updatedAt);
  }

  get master() {
    let masterId = null;
    const peerList = Object.keys(this.peerMap ?? {})
      .map((clientId) => ({
        clientId,
        updateTime: (this.peerMap ?? {})[clientId],
      }))
      .sort((a, b) => moment(b.updateTime).diff(a.updateTime));
    if (peerList.length > 0) {
      masterId = peerList[0].clientId;
    }
    return masterId;
  }

  get isGuest(): boolean {
    return !/^[a-zA-Z0-9.!#$%&â€™*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(
      this.email ?? ""
    );
  }

  get displayEmail(): string {
    return this.isGuest ? "Guest" : this.email!;
  }

  compareTo(anotherUser: User | undefined | null): boolean {
    let areEqual = false;
    if (anotherUser !== undefined && anotherUser !== null) {
      const diff = DeepDiff.diff(
        this.toJSON(true),
        anotherUser?.toJSON(true) ?? {}
      );
      areEqual = !diff || diff?.length === 0;
    }
    return areEqual;
  }

  toJSON(forCompare?: boolean): UserInterface {
    const json: any = Object.assign({}, this);
    json.taskMap = Object.entries(json.taskMap ?? {});
    delete json.stateService;
    if (!forCompare) {
      delete json._id;
    }
    return json;
  }
}


export const userDisplayName = (user: Partial<User> | undefined) => {
  const names: string[] = [];
  if (user?.firstName && user?.firstName?.trim() !== '') {
    names.push(user?.firstName?.trim());
  }
  if (user?.lastName && user?.lastName?.trim() !== '') {
    names.push(user.lastName.trim());
  }
  return names.length > 0 ? names.join(' ') : (user?.email || '');
};
